import {
    Championship,
    Collections,
    PenaltyPoints,
    Track,
    useCollection,
    useRounds,
} from "api";
import MaterialTable, { Column } from "material-table";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { BoxCard, FormButton, LoggedUserContext } from "ui/componentes";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import { ChampionshipDriver } from "../../../api";
import { useSnackbar } from "notistack";
import { functions } from "../../../config/firebaseConfig";
import { Chip } from "@material-ui/core";
import { blue, red } from "@material-ui/core/colors";

interface UseUpdatePenaltyPointsResults {
    updatePenaltyPoints: (championship: Championship) => void;
    updating: boolean;
}

const useUpdatePenaltyPoints = (): UseUpdatePenaltyPointsResults => {
    const [updating, setUpdating] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const updatePenaltyPoints = useCallback(
        (championship: Championship) => {
            setUpdating(true);
            functions
                .httpsCallable("penalty_points-calculatePenaltyPoints")({
                    championshipId: championship.id,
                })
                .then(() => {
                    enqueueSnackbar(
                        "Pontos por penalidades atualizados com sucesso!",
                        {
                            variant: "success",
                        }
                    );
                })
                .catch(() => {
                    enqueueSnackbar(
                        "Não possível atualizar os pontos por penalidades!",
                        { variant: "error" }
                    );
                })
                .finally(() => setUpdating(false));
        },
        [enqueueSnackbar]
    );

    return {
        updatePenaltyPoints,
        updating,
    };
};

interface PenaltyPointsTableProps {
    championship: Championship;
    drivers: ChampionshipDriver[];
}

const PenaltyPointsTable: React.FC<PenaltyPointsTableProps> = ({
    championship,
    drivers,
}) => {
    const loggedUser = useContext(LoggedUserContext);

    const {
        updating: updatingPenaltyPoints,
        updatePenaltyPoints,
    } = useUpdatePenaltyPoints();

    const tracksById =
        useCollection<{ [key: string]: Track }>({
            collection: Collections.TRACKS,
        }) || {};

    const rounds = useRounds({ championshipId: championship.id });

    const penaltyPoints = useCollection<PenaltyPoints[]>(
        {
            collection: Collections.PENALTY_POINTS,
            where: ["championshipId", "==", championship.id || "-1"],
        },
        true
    );

    const totalByIRacingId = useMemo(() => {
        const totalByIRacingId = {};
        if (penaltyPoints && penaltyPoints.length) {
            Object.keys(penaltyPoints[0].pointsByRound || {}).forEach(
                (roundId) => {
                    const pointsByIRacingId =
                        penaltyPoints[0].pointsByRound[roundId] || {};
                    Object.keys(pointsByIRacingId).forEach((iRacingId) => {
                        totalByIRacingId[iRacingId] =
                            (totalByIRacingId[iRacingId] || 0) +
                            ((pointsByIRacingId[iRacingId] || {}).points || 0);
                    });
                }
            );
        }
        return totalByIRacingId;
    }, [penaltyPoints]);

    const sortedDrivers = useMemo(
        () =>
            drivers
                .filter((driver) => totalByIRacingId[driver.iracingId || ""])
                .sort((d1, d2) => (d1.displayName < d2.displayName ? -1 : 1)),
        [drivers, totalByIRacingId]
    );

    const columns = useMemo<Column<ChampionshipDriver>[]>(() => {
        if (!rounds.length) return [];

        const columns: Column<ChampionshipDriver>[] = [
            {
                field: "usedNumber",
                title: "Nº",
                width: 50,
                type: "numeric",
                cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                render: (driver) => `#${driver.usedNumber}`,
            },
            {
                field: "driver",
                title: "Piloto",
                cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                render: (driver) => `${driver.displayName}`,
            },
        ];

        columns.push(
            ...rounds.map<Column<ChampionshipDriver>>((round, index) => ({
                field: `round${index + 1}`,
                title: tracksById[round.trackId]?.friendlyName,
                align: "center",
                cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                render: (driver) => {
                    // return "-";
                    if (!penaltyPoints || !penaltyPoints.length) return "-";

                    const suspendedDrivers =
                        (penaltyPoints[0].suspendedDriversByRound || {})[
                            round.id || ""
                        ] || {};

                    const startFromPitLaneDrivers =
                        (penaltyPoints[0].startFromPitLaneDriversByRound || {})[
                            round.id || ""
                        ] || {};

                    if (suspendedDrivers[driver.iracingId || ""]) {
                        return (
                            <Chip
                                label="Suspenso"
                                size="small"
                                style={{
                                    backgroundColor: red["500"],
                                    color: "white",
                                }}
                            />
                        );
                    }

                    if (startFromPitLaneDrivers[driver.iracingId || ""]) {
                        return (
                            <Chip
                                label="Largar dos boxes"
                                size="small"
                                style={{
                                    backgroundColor: blue["500"],
                                    color: "white",
                                }}
                            />
                        );
                    }

                    const pointsByUser =
                        penaltyPoints[0].pointsByRound[round.id || ""] || {};

                    const points =
                        (pointsByUser[driver.iracingId || ""] || {}).points ||
                        0;
                    return points && points > 0 ? points : " - ";
                },
            }))
        );

        columns.push({
            field: "total",
            title: "Total",
            align: "center",
            width: 50,
            cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
            render: (driver) => totalByIRacingId[driver.iracingId || ""] || "-",
        });

        return columns;
    }, [penaltyPoints, rounds, totalByIRacingId, tracksById]);

    return (
        <MaterialTable
            title={`Pontos por punições`}
            components={{
                Container: BoxCard,
            }}
            columns={columns}
            data={sortedDrivers}
            localization={LocalizationPTBr}
            actions={[
                {
                    isFreeAction: true,
                    icon: () => (
                        <FormButton
                            color="primary"
                            loading={updatingPenaltyPoints}
                            onClick={() => {
                                updatePenaltyPoints(championship);
                            }}
                        >
                            Recalcular pontos por penalidades
                        </FormButton>
                    ),
                    onClick: () => "",
                    disabled: updatingPenaltyPoints,
                    hidden: !loggedUser.isAdmin,
                },
            ]}
            options={{
                draggable: false,
                sorting: false,
                search: false,
                paging: false,
                rowStyle: () => ({
                    padding: 0,
                    fontSize: "0.857rem",
                }),
            }}
        />
    );
};

export { PenaltyPointsTable };
